import { z } from 'zod';

const uuid = z.string().uuid();

/**
 * A file uploaded through `POST /uploads/presign` and then linked to a row: a task's attachment
 * (purpose `assignment`), a student's submission (`submission`) or a topic material (`material`).
 * The key must sit under the caller's school and that purpose; the API checks both.
 */
export const uploadedFileSchema = z.object({
  key: z.string().min(1).max(500),
  filename: z.string().trim().min(1).max(200),
  contentType: z
    .string()
    .trim()
    .regex(/^[\w.+-]+\/[\w.+-]+$/, 'contentType must be a media type'),
  sizeBytes: z
    .number()
    .int()
    .positive()
    .max(50 * 1024 * 1024),
});

/**
 * The moment a task is due, with its offset (`2026-10-02T23:59:00-05:00`).
 */
const dueAt = z.string().datetime({ offset: true });

/**
 * Body of `POST /assignments`: a task for one group and subject (#149). Published unless
 * `status` says `draft`; the topic, when given, must be of that subject and the group's grade.
 */
export const createAssignmentInputSchema = z.object({
  groupId: uuid,
  subjectId: uuid,
  title: z.string().trim().min(1).max(200),
  instructions: z.string().trim().max(10_000).optional(),
  dueAt,
  topicId: uuid.nullable().optional(),
  attachment: uploadedFileSchema.nullable().optional(),
  status: z.enum(['draft', 'published']).optional(),
});

/**
 * Body of `PATCH /assignments/{id}`: any field of a task that is not closed; `status` publishes
 * a draft or takes a task without submissions back to draft.
 */
export const updateAssignmentInputSchema = z
  .object({
    title: z.string().trim().min(1).max(200),
    instructions: z.string().trim().max(10_000),
    dueAt,
    topicId: uuid.nullable(),
    attachment: uploadedFileSchema.nullable(),
    status: z.enum(['draft', 'published']),
  })
  .partial();

/**
 * Query of `GET /assignments`: the tasks of a group, optionally of one subject.
 */
export const assignmentsQuerySchema = z.object({
  groupId: uuid,
  subjectId: uuid.optional(),
});

/**
 * Where a task stands for one student: `pending` until its due moment, `overdue` after it (or
 * when it closed without their work), `done` once they marked it done or submitted it (a `redo`
 * review takes it back to pending or overdue).
 */
export const STUDENT_ASSIGNMENT_STATES = ['pending', 'overdue', 'done'] as const;

/**
 * Query of `GET /students/{id}/assignments`: optionally one state.
 */
export const studentAssignmentsQuerySchema = z.object({
  state: z.enum(STUDENT_ASSIGNMENT_STATES).optional(),
});

/**
 * Body of `PUT /assignments/{id}/submission`: the student marks the task done (no content) or
 * submits text and/or a file. Allowed until the teacher reviews it, and again after a `redo`.
 */
export const putSubmissionInputSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('done') }),
  z
    .object({
      status: z.literal('submitted'),
      text: z.string().trim().max(20_000).optional(),
      file: uploadedFileSchema.optional(),
    })
    .refine((body) => Boolean(body.text) || Boolean(body.file), {
      path: ['text'],
      message: 'Give text, a file or both',
    }),
]);

/**
 * Body of `POST /submissions/{id}/review`: `accepted` or `redo`, with optional feedback.
 */
export const reviewSubmissionInputSchema = z.object({
  status: z.enum(['accepted', 'redo']),
  feedback: z.string().trim().max(5_000).nullable().optional(),
});

/**
 * Body of `POST /curriculum/topics/{id}/materials`: a link (http or https) or an uploaded file
 * (purpose `material`).
 */
export const createTopicMaterialInputSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('link'),
    title: z.string().trim().min(1).max(200),
    url: z
      .string()
      .trim()
      .url()
      .max(2_000)
      .refine((value) => /^https?:\/\//i.test(value), 'Only http and https links'),
  }),
  z.object({
    kind: z.literal('file'),
    title: z.string().trim().min(1).max(200).optional(),
    file: uploadedFileSchema,
  }),
]);

/**
 * Query of `GET /students/{id}/topics`: a period of the current school year; by default the
 * current period.
 */
export const studentTopicsQuerySchema = z.object({
  periodId: uuid.optional(),
});

/**
 * Body of the create route.
 */
export type CreateAssignmentInput = z.input<typeof createAssignmentInputSchema>;

/**
 * Body of the edit route.
 */
export type UpdateAssignmentInput = z.input<typeof updateAssignmentInputSchema>;

/**
 * Query of the teacher's and staff's list.
 */
export type AssignmentsQuery = z.infer<typeof assignmentsQuerySchema>;

/**
 * Query of a student's list.
 */
export type StudentAssignmentsQuery = z.infer<typeof studentAssignmentsQuerySchema>;

/**
 * Body of the student's submission.
 */
export type PutSubmissionInput = z.input<typeof putSubmissionInputSchema>;

/**
 * Body of the teacher's review.
 */
export type ReviewSubmissionInput = z.input<typeof reviewSubmissionInputSchema>;

/**
 * Body of a new topic material.
 */
export type CreateTopicMaterialInput = z.input<typeof createTopicMaterialInputSchema>;

/**
 * Query of a student's topics.
 */
export type StudentTopicsQuery = z.infer<typeof studentTopicsQuerySchema>;

/**
 * A linked file as the API lists it; the download link is a separate call.
 */
export type UploadedFileView = {
  filename: string;
  contentType: string;
  sizeBytes: number;
};

/**
 * A short-lived download link, issued after the caller's scope was checked.
 */
export type FileLink = {
  url: string;
  expiresAt: string;
  filename: string;
};

/**
 * A task's lifecycle.
 */
export type AssignmentStatus = 'draft' | 'published' | 'closed';

/**
 * Where a task stands for one student; see `STUDENT_ASSIGNMENT_STATES`.
 */
export type StudentAssignmentState = (typeof STUDENT_ASSIGNMENT_STATES)[number];

/**
 * A task with the names the app shows, so no role needs the school's catalog reads.
 */
export type AssignmentView = {
  id: string;
  schoolYearId: string;
  groupId: string;
  groupName: string;
  subjectId: string;
  subjectName: string;
  topicId: string | null;
  topicTitle: string | null;
  title: string;
  instructions: string;
  dueAt: string;
  status: AssignmentStatus;
  publishedAt: string | null;
  closedAt: string | null;
  authorUserId: string | null;
  authorName: string | null;
  attachment: UploadedFileView | null;
  createdAt: string;
};

/**
 * How the students of the group stand on a task: `pending` have nothing yet, `late` counts
 * submissions (of any state) that arrived after the due moment.
 */
export type AssignmentCounts = {
  students: number;
  pending: number;
  done: number;
  submitted: number;
  reviewed: number;
  late: number;
};

/**
 * A task in the teacher's and staff's list, with its counts.
 */
export type AssignmentListItem = AssignmentView & { counts: AssignmentCounts };

/**
 * A submission's state: `pending` has no row yet.
 */
export type SubmissionState = 'pending' | 'done' | 'submitted' | 'reviewed';

/**
 * The teacher's last review of a submission; kept when the student resubmits after `redo`.
 */
export type SubmissionReview = {
  status: 'accepted' | 'redo';
  feedback: string | null;
  reviewedAt: string;
  reviewerName: string | null;
};

/**
 * One student's work on a task.
 */
export type SubmissionView = {
  /**
   * Null while the student has done nothing (`pending`).
   */
  id: string | null;
  assignmentId: string;
  studentId: string;
  studentName: string;
  state: SubmissionState;
  text: string | null;
  file: UploadedFileView | null;
  submittedAt: string | null;
  /**
   * Submitted after the task's due moment.
   */
  late: boolean;
  attempts: number;
  review: SubmissionReview | null;
};

/**
 * `GET /assignments/{id}/submissions`: the task and one row per active student of its group.
 */
export type AssignmentSubmissionsView = {
  assignment: AssignmentView;
  counts: AssignmentCounts;
  items: SubmissionView[];
};

/**
 * One task as a student or their guardian sees it.
 */
export type StudentAssignmentItem = {
  assignment: AssignmentView;
  state: StudentAssignmentState;
  submission: SubmissionView | null;
};

/**
 * `GET /students/{id}/assignments`: the published and closed tasks of the student's current
 * group, soonest due first.
 */
export type StudentAssignmentsView = {
  studentId: string;
  groupId: string | null;
  groupName: string | null;
  items: StudentAssignmentItem[];
};

/**
 * One of the teacher's group × subject pairs this year (`subjectId` null for a homeroom-only
 * assignment).
 */
export type TeacherClass = {
  groupId: string;
  groupName: string;
  gradeId: string;
  gradeName: string;
  subjectId: string | null;
  subjectName: string | null;
  homeroom: boolean;
};

/**
 * `GET /me/classes`: the caller's teaching pairs in the current school year; empty for anyone
 * who does not teach.
 */
export type MyClassesView = {
  schoolYearId: string | null;
  items: TeacherClass[];
};

/**
 * A material of a plan topic: a link (with its URL) or a file (download through its route).
 */
export type TopicMaterialView = {
  id: string;
  topicId: string;
  kind: 'file' | 'link';
  title: string;
  url: string | null;
  file: UploadedFileView | null;
  addedByName: string | null;
  createdAt: string;
};

/**
 * What a material's route returns: the link's URL or a presigned link to the file.
 */
export type TopicMaterialLink = {
  kind: 'file' | 'link';
  url: string;
  expiresAt: string | null;
  filename: string | null;
};

/**
 * A topic of the plan as a student reviews it: logros, how the class log covered it in the
 * student's group, and its materials.
 */
export type StudentTopic = {
  id: string;
  title: string;
  logros: string[];
  dbaReference: string | null;
  coverage: { sessions: number; lastDate: string | null };
  materials: TopicMaterialView[];
};

/**
 * `GET /students/{id}/topics`: the topics of one period for the student's grade, per subject.
 */
export type StudentTopicsView = {
  studentId: string;
  groupId: string | null;
  groupName: string | null;
  gradeId: string | null;
  gradeName: string | null;
  period: { id: string; name: string; position: number } | null;
  subjects: { subjectId: string; subjectName: string; topics: StudentTopic[] }[];
};
